/**
 * Loading every receipt behind the receipts table.
 *
 * Kept free of imports so the paging rules can be unit-tested without a
 * browser (see tests/receipt-pages.spec.ts).
 */

/** One page of the receipts list endpoint, as far as paging cares. */
export interface ReceiptPage<T> {
  results: T[];
  total_count: number;
  has_more: boolean;
}

interface DatedRow {
  id: string;
  date: string | null;
}

/**
 * Load every receipt that matches the table's filters.
 *
 * The endpoint pages by offset over `date DESC`, and many receipts share a
 * date. Two offset pages can disagree about the order inside a tie, so one
 * receipt arrives twice and another never arrives. Instead of trusting
 * offsets, each further request asks for "everything on or before the oldest
 * date seen so far" and rows are merged by id. The date only moves backwards,
 * so no receipt can fall between two requests.
 *
 * Undated receipts sort last, and the endpoint leaves them out whenever a
 * date bound is sent, so they are fetched at the end with offset requests
 * that start after the dated rows.
 *
 * `maxRows` keeps a very large list from freezing the page. Whenever
 * `results.length` ends up below `total_count` (the cap, or more than one
 * page of receipts on a single date) the caller says so on screen.
 */
export async function loadAllReceipts<
  T extends DatedRow,
  R extends ReceiptPage<T>,
>(
  fetchPage: (args: { to: string | null; offset: number }) => Promise<R>,
  {
    to,
    hasDateBounds,
    maxRows,
  }: { to: string | null; hasDateBounds: boolean; maxRows: number },
): Promise<R> {
  const first = await fetchPage({ to, offset: 0 });
  const byId = new Map<string, T>();
  const add = (rows: T[]) => {
    for (const row of rows) byId.set(row.id, row);
  };
  add(first.results);

  let page: ReceiptPage<T> = first;
  let cursor = to;
  let stuckOnOneDate = false;
  while (page.has_more && byId.size < maxRows) {
    const oldest = page.results[page.results.length - 1];
    // An undated last row means every dated receipt is already loaded.
    if (!oldest?.date) break;
    if (oldest.date === cursor) {
      // A full page of receipts on one date: there is no older date to ask for.
      stuckOnOneDate = true;
      break;
    }
    cursor = oldest.date;
    page = await fetchPage({ to: cursor, offset: 0 });
    add(page.results);
  }

  if (
    !hasDateBounds &&
    !stuckOnOneDate &&
    byId.size < first.total_count &&
    byId.size < maxRows
  ) {
    // Whatever is still missing sorts after every dated receipt.
    let offset = 0;
    for (const row of byId.values()) if (row.date) offset += 1;
    for (;;) {
      const tail = await fetchPage({ to: null, offset });
      add(tail.results);
      if (!tail.has_more || tail.results.length === 0) break;
      if (byId.size >= maxRows) break;
      offset += tail.results.length;
    }
  }

  return { ...first, results: Array.from(byId.values()).slice(0, maxRows) };
}
